import clsx from 'clsx';
import { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router';

import type { User } from '~/types/User';

import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import {
  componentConfig,
  djsConfig,
} from '~/components/App/AppContents/AppContents.config';
import { MAIN_CONTENT_ID } from '~/components/App/AppContents/constants';
import { AppData } from '~/components/App/AppData';
import { Box } from '~/eds/src/components/Box/Box';
import { AppBreadcrumb } from '~/features/app/AppBreadcrumb';
import { AppBreadcrumbSlotProvider } from '~/features/app/AppBreadcrumbSlotContext';
import { AppPageNavbar } from '~/features/app/AppPageNavbar';
import { AppSidebarNav } from '~/features/app/AppSidebarNav';
import { SidebarNavProvider } from '~/features/app/SidebarNavContext';
import { SidebarNavOnboardingModal } from '~/features/app/SidebarNavOnboardingModal';
import { useSidebarNavPreference } from '~/features/app/SidebarNavPreferenceContext';
import { AskAiProvider } from '~/features/ask-anything/contexts/AskAiContext';
import { NotificationCenterProvider } from '~/features/notifications/NotificationCenterContext';
import { AiWorkflowBuildsNotificationSource } from '~/features/workflows-clm-evolution/ai-builds/AiWorkflowBuildsNotificationSource';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { MetricsService } from '~/services/MetricsService/MetricsService';

import './LoggedInLayout.css';

const UploadInfoPanel = lazy(
  () => import('~/components/Shared/UploadInfoPanel/UploadInfoPanel'),
);
const DropzoneComponent = lazy(() => import('react-dropzone-component'));
const ChatBotPanel = lazy(() =>
  import('~/features/ask-anything/chat-bot-panel/ChatBotPanel').then((m) => ({
    default: m.ChatBotPanel,
  })),
);

const hasBoundedLayout = (handle: unknown) =>
  typeof handle === 'object' &&
  handle !== null &&
  'layout' in handle &&
  handle.layout === 'bounded';

const hasWorkspaceLayout = (handle: unknown) =>
  typeof handle === 'object' &&
  handle !== null &&
  'layout' in handle &&
  handle.layout === 'workspace';

const hasChromelessLayout = (handle: unknown) =>
  typeof handle === 'object' &&
  handle !== null &&
  'layout' in handle &&
  handle.layout === 'chromeless';

/**
 * Layout route for authenticated app pages. Owns the logged-in chrome
 * (navbar, upload panel, auth-required modal, dropzone, per-page padding)
 * and gates access — unauthenticated visitors get redirected to /login.
 *
 * Renders one of two nav shells around the routed content: the legacy top
 * navbar, or the vertical sidebar nav when the `SidebarNav` flag is on and
 * the user has opted in (see `SidebarNavPreferenceContext`).
 *
 * Replaces the auth-conditional rendering that used to live in
 * `AppContents` and the bespoke pathname-switching in `PrivateRoute`.
 */
export function LoggedInLayout() {
  const location = useLocation();
  const matches = useMatches();
  const { isLoggedIn: isUserLoggedIn, requiresReauth } =
    useAuthenticationStore();
  const currentUser = useSelector(
    slices.currentUser.selectors.selectCurrentUser,
  );
  const user: User | undefined =
    Object.keys(currentUser).length > 0 ? (currentUser as User) : undefined;

  const isSidebarNavAvailable = useFlag(FlagType.SidebarNav);
  const { isOptedIn } = useSidebarNavPreference();
  const useSidebarLayout = isSidebarNavAvailable && isOptedIn;
  const usesWorkspaceLayout = matches.some((match) =>
    hasWorkspaceLayout(match.handle),
  );
  const usesChromelessLayout = matches.some((match) =>
    hasChromelessLayout(match.handle),
  );
  // `workspace` additionally goes full-bleed (see `routeContent` below); both
  // bound the route's height to the viewport so a page can scroll internally.
  const usesBoundedHeight =
    usesWorkspaceLayout ||
    matches.some((match) => hasBoundedLayout(match.handle));

  useEffect(() => {
    if (isUserLoggedIn) {
      MetricsService.appReady();
    }
  }, [isUserLoggedIn]);

  if (!isUserLoggedIn) {
    // Keep the current URL mounted (rendering nothing) while the auth-required
    // modal owned by AppContents handles re-auth. Skip on
    // /folders-permission-check, where the modal itself is suppressed
    // (EKP-24875).
    if (
      requiresReauth &&
      !location.pathname.includes('folders-permission-check')
    ) {
      return null;
    }
    return (
      <Navigate to={RoutePathType.Login} state={{ from: location }} replace />
    );
  }

  // Chrome-independent app globals, mounted the same way under either nav
  // shell so route content behaves identically regardless of the layout.
  // Upload/dropzone/chat are skipped on chromeless pages (e.g. the esign
  // redirect tab), which have no upload or chat-support UI to justify
  // pulling in their chunks and competing with that page's own critical path.
  const globals = (
    <>
      <AppData />
      <AiWorkflowBuildsNotificationSource />
      {!usesChromelessLayout && (
        <>
          {user && (
            <Suspense fallback={null}>
              {/* `onRef` is an intentional no-op: UploadInfoPanel's class-component
                  API calls `props.onRef(this)` unconditionally, but nothing ever
                  read the ref. Drop-in equivalent until UploadInfoPanel migrates
                  to TSX under EKP-47979. */}
              <UploadInfoPanel user={user} onRef={() => undefined} />
            </Suspense>
          )}
          <Suspense fallback={null}>
            <DropzoneComponent
              className="app-dropzone"
              config={componentConfig}
              djsConfig={djsConfig}
            />
          </Suspense>
          <Suspense fallback={null}>
            <ChatBotPanel />
          </Suspense>
        </>
      )}
    </>
  );

  const routeContent = (
    <Box
      className={clsx({
        'app-shell-layout__route-content--bounded-height': usesBoundedHeight,
      })}
      px={usesWorkspaceLayout ? 0 : 8}
      py={usesWorkspaceLayout ? 0 : 3}
    >
      <Outlet />
    </Box>
  );

  let content;
  if (usesChromelessLayout) {
    content = (
      <main id={MAIN_CONTENT_ID}>
        <Outlet />
      </main>
    );
  } else if (useSidebarLayout) {
    content = (
      <AppBreadcrumbSlotProvider>
        <div className="app-shell-layout">
          <AppBreadcrumb />
          <div className="app-shell-layout__body">
            <AppSidebarNav />
            <SidebarNavOnboardingModal />
            <div
              className={clsx('app-shell-layout__content', {
                'app-shell-layout__content--bounded-height': usesBoundedHeight,
              })}
            >
              <main
                id={MAIN_CONTENT_ID}
                className={clsx('app-shell-layout__main', {
                  'app-shell-layout__main--bounded-height': usesBoundedHeight,
                  'app-shell-layout__main--workspace': usesWorkspaceLayout,
                })}
              >
                {routeContent}
              </main>
            </div>
          </div>
        </div>
      </AppBreadcrumbSlotProvider>
    );
  } else {
    content = (
      <div
        className={clsx('app-shell-layout__legacy', {
          'app-shell-layout__legacy--bounded-height': usesBoundedHeight,
        })}
      >
        <AppPageNavbar />
        <main
          id={MAIN_CONTENT_ID}
          className={clsx({
            'app-shell-layout__legacy-main--bounded-height': usesBoundedHeight,
          })}
        >
          {routeContent}
        </main>
      </div>
    );
  }

  return (
    <AskAiProvider>
      <NotificationCenterProvider>
        <SidebarNavProvider>
          {content}
          {globals}
        </SidebarNavProvider>
      </NotificationCenterProvider>
    </AskAiProvider>
  );
}
